import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Send, AlertTriangle } from 'lucide-react';

const labels = {
  fr: {
    title: 'Vous aussi, rejoignez la Résistance !',
    subtitle: "Laissez-nous votre adresse mail et un agent vous recontactera sous 48h (si notre VPN fonctionne).",
    placeholder: 'votre.email@exemple.com',
    submit: 'Envoyer (à vos risques et périls)',
    sending: 'Transmission cryptée en cours...',
    success: "C'est fait ! Vérifiez votre boîte mail... et vos spams. La CIA surveille tout.",
    error: "Erreur de transmission. Igor a encore débranché le serveur pour brancher sa bouilloire.",
    warning: "En soumettant ce formulaire, vous acceptez d'être fiché par au moins 4 services de renseignement.",
    close: 'Fermer'
  },
  ru: {
    title: 'Присоединяйтесь к Сопротивлению!',
    subtitle: 'Оставьте свой email, и агент свяжется с вами в течение 48 часов (если VPN работает).',
    placeholder: 'ваш.email@пример.com',
    submit: 'Отправить (на свой страх и риск)',
    sending: 'Зашифрованная передача...',
    success: 'Готово! Проверьте почту... и спам. ЦРУ следит за всем.',
    error: 'Ошибка передачи. Игорь опять отключил сервер ради чайника.',
    warning: 'Отправляя эту форму, вы соглашаетесь быть в базах минимум 4 спецслужб.',
    close: 'Закрыть'
  },
  ko: {
    title: '저항에 동참하세요!',
    subtitle: '이메일을 남겨주시면 48시간 이내에 요원이 연락드립니다 (VPN이 작동하면).',
    placeholder: 'your.email@example.com',
    submit: '보내기 (위험을 감수하고)',
    sending: '암호화 전송 중...',
    success: '완료! 메일함을 확인하세요... 스팸도요. CIA가 모든 걸 감시합니다.',
    error: '전송 오류. 이고르가 또 주전자 때문에 서버를 뽑았습니다.',
    warning: '이 양식을 제출하면 최소 4개 정보기관에 등록됩니다.',
    close: '닫기'
  },
  zh: {
    title: '加入抵抗运动！',
    subtitle: '留下您的邮箱，特工将在48小时内联系您（如果VPN正常的话）。',
    placeholder: 'your.email@example.com',
    submit: '发送（后果自负）',
    sending: '加密传输中...',
    success: '完成！检查您的邮箱...还有垃圾邮件。CIA在监视一切。',
    error: '传输错误。伊戈尔又为了烧水壶拔掉了服务器。',
    warning: '提交此表格即表示您同意被至少4个情报机构存档。',
    close: '关闭'
  }
};

const ContactPopup = () => {
  const { language } = useLanguage();
  const l = labels[language] || labels.fr;
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const dismissed = sessionStorage.getItem('contact_popup_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setVisible(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setVisible(false);
    sessionStorage.setItem('contact_popup_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setStatus('sent');
        await new Promise(r => setTimeout(r, 5000));
        handleClose();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 animate-fadeIn">
      <div className="relative w-full max-w-md bg-gradient-to-br from-gray-900 via-red-950/20 to-gray-900 border-2 border-red-500/50 rounded-xl shadow-[0_0_60px_rgba(220,38,38,0.2)] p-8">
        {/* Close */}
        <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors" data-testid="contact-popup-close">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-3">&#9773;</div>
          <h2 className="text-2xl font-bold text-red-400 mb-2">{l.title}</h2>
          <p className="text-sm text-gray-400 leading-relaxed">{l.subtitle}</p>
        </div>

        {status === 'sent' ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-4">&#9993;</div>
            <p className="text-green-400 font-medium">{l.success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={l.placeholder}
              required
              className="w-full px-4 py-3 bg-black/50 border border-red-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              data-testid="contact-email-input"
            />

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full px-4 py-3 bg-red-700 hover:bg-red-600 disabled:bg-red-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
              data-testid="contact-submit-btn"
            >
              {status === 'sending' ? (
                <>{l.sending}</>
              ) : (
                <><Send className="w-4 h-4" /> {l.submit}</>
              )}
            </button>

            {status === 'error' && (
              <p className="text-red-400 text-sm text-center">{l.error}</p>
            )}

            <div className="flex items-start gap-2 mt-4">
              <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-600/80 italic">{l.warning}</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactPopup;
